import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileDown, CheckCircle, Send, Package, Trash2, Loader2, Mail, Link2, Eye, Pencil, Search } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatCurrency, formatDate, CURRENCY_LIST } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_ITEM = { description: '', hsn_sac_code: '', quantity: 1, unit_price: 0, tax_type: 'none', custom_tax_name: '', tax_rate: 0, cgst_rate: 0, sgst_rate: 0, igst_rate: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, tax_amount: 0, amount: 0 };
const EMPTY_FORM = { vendor_id: '', vendor_name: '', vendor_email: '', billing_address: '', amount: 0, subtotal: 0, tax_amount: 0, currency: 'USD', description: '', bank_details: '', notes: '', items: [{ ...EMPTY_ITEM }] };
const TAX_TYPES = [
  { value: 'none', label: 'No Tax' },
  { value: 'igst', label: 'IGST' },
  { value: 'cgst_sgst', label: 'CGST + SGST' },
  { value: 'custom', label: 'Custom Tax' },
];
const PO_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent' },
  { value: 'completed', label: 'Completed' },
];

export default function PurchaseOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmSendPo, setConfirmSendPo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    try { const { data } = await api.get('/purchase-orders'); setOrders(data); } catch { toast.error('Failed to load orders'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (statusFilter !== 'all') {
      list = list.filter(po => po.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(po =>
        (po.po_number || '').toLowerCase().includes(q) ||
        (po.vendor_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, searchQuery, statusFilter]);

  const fetchVendors = useCallback(async () => {
    try { const { data } = await api.get('/vendors'); setVendors(data); } catch {}
  }, []);

  const openCreateDialog = async () => {
    setEditId(null);
    const newForm = { ...EMPTY_FORM };
    fetchVendors();
    try {
      const { data } = await api.get('/company-settings');
      if (data) {
        const bankParts = [data.bank_name, data.bank_account ? `A/C: ${data.bank_account}` : '', data.bank_ifsc ? `IFSC: ${data.bank_ifsc}` : '', data.bank_branch ? `Branch: ${data.bank_branch}` : ''].filter(Boolean);
        if (bankParts.length) newForm.bank_details = bankParts.join('\n');
      }
    } catch {}
    setForm(newForm);
    setDialogOpen(true);
  };

  const openEditDialog = async (po) => {
    setEditId(po.id);
    fetchVendors();
    setForm({
      vendor_id: po.vendor_id || '', vendor_name: po.vendor_name || '', vendor_email: po.vendor_email || '',
      billing_address: po.billing_address || '', amount: po.amount || 0, subtotal: po.subtotal || 0,
      tax_amount: po.tax_amount || 0, currency: po.currency || 'USD', description: po.description || '',
      bank_details: po.bank_details || '', notes: po.notes || '',
      items: (po.items && po.items.length > 0) ? po.items : [{ ...EMPTY_ITEM }]
    });
    setDialogOpen(true);
  };

  const handleVendorSelect = (vendorId) => {
    const v = vendors.find(x => x.id === vendorId);
    if (v) {
      const addr = [v.address, v.city, v.state, v.country].filter(Boolean).join(', ');
      setForm({ ...form, vendor_id: vendorId, vendor_name: v.name, vendor_email: v.email || '', billing_address: addr });
    }
  };

  const recalcTotals = (items) => {
    const subtotal = items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unit_price)), 0);
    const taxTotal = items.reduce((s, i) => s + Number(i.tax_amount), 0);
    return { subtotal, tax_amount: taxTotal, amount: subtotal + taxTotal };
  };

  const calcItemTax = (item) => {
    const lineTotal = Number(item.quantity) * Number(item.unit_price);
    const rate = Number(item.tax_rate);
    let cgst_rate = 0, sgst_rate = 0, igst_rate = 0, cgst_amount = 0, sgst_amount = 0, igst_amount = 0, tax_amount = 0;
    if (item.tax_type === 'igst') {
      igst_rate = rate;
      igst_amount = lineTotal * (rate / 100);
      tax_amount = igst_amount;
    } else if (item.tax_type === 'cgst_sgst') {
      cgst_rate = rate / 2;
      sgst_rate = rate / 2;
      cgst_amount = lineTotal * (cgst_rate / 100);
      sgst_amount = lineTotal * (sgst_rate / 100);
      tax_amount = cgst_amount + sgst_amount;
    } else if (item.tax_type === 'custom') {
      tax_amount = lineTotal * (rate / 100);
    }
    return { ...item, cgst_rate, sgst_rate, igst_rate, cgst_amount, sgst_amount, igst_amount, tax_amount, amount: lineTotal + tax_amount };
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'tax_type') {
      items[idx].tax_rate = items[idx].tax_type === 'none' ? 0 : items[idx].tax_rate;
    }
    if (['quantity', 'unit_price', 'tax_rate', 'tax_type'].includes(field)) {
      items[idx] = calcItemTax(items[idx]);
    }
    const totals = recalcTotals(items);
    setForm({ ...form, items, ...totals });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] });
  const removeItem = (idx) => {
    const items = form.items.filter((_, i) => i !== idx);
    const totals = recalcTotals(items);
    setForm({ ...form, items, ...totals });
  };

  const handleSave = async () => {
    if (!form.vendor_id) { toast.error('Please select a registered vendor'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/purchase-orders/${editId}`, form);
        toast.success('Purchase order updated');
      } else {
        await api.post('/purchase-orders', form);
        toast.success('Purchase order created');
      }
      setDialogOpen(false); setEditId(null); setForm({ ...EMPTY_FORM }); fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save PO'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/purchase-orders/${id}/status`, { status }); toast.success(`PO ${status}`); fetchOrders(); }
    catch { toast.error('Failed to update status'); }
  };

  const deleteOrder = async (id) => {
    try { await api.delete(`/purchase-orders/${id}`); toast.success('PO deleted'); fetchOrders(); }
    catch { toast.error('Failed to delete'); }
  };

  const downloadPdf = async (id, number) => {
    try {
      const resp = await fetch(`${API_URL}/api/purchase-orders/${id}/pdf`, { credentials: 'include' });
      const blob = await resp.blob(); const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${number}.pdf`; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const openPreview = async (id, number) => {
    try {
      const resp = await fetch(`${API_URL}/api/purchase-orders/${id}/pdf`, { credentials: 'include' });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewNumber(number);
      setPreviewOpen(true);
    } catch { toast.error('Failed to load preview'); }
  };

  const sendPoWithUploadLink = async (id) => {
    try {
      const { data } = await api.post(`/purchase-orders/${id}/send-upload-link`);
      toast.success(data.message);
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send PO'); }
  };

  const copyUploadLink = async (id) => {
    try {
      const { data } = await api.post(`/purchase-orders/${id}/generate-upload-link`);
      await navigator.clipboard.writeText(data.upload_url);
      toast.success('Upload link copied to clipboard');
    } catch { toast.error('Failed to generate link'); }
  };

  const handleMarkPoAsSent = (po) => {
    setConfirmSendPo(po);
    setConfirmSendOpen(true);
  };

  const confirmMarkPoAsSent = async (alsoEmail) => {
    if (!confirmSendPo) return;
    await updateStatus(confirmSendPo.id, 'sent');
    if (alsoEmail) {
      sendPoWithUploadLink(confirmSendPo.id);
    }
    setConfirmSendOpen(false);
    setConfirmSendPo(null);
  };

  const statusActions = (po) => {
    switch (po.status) {
      case 'draft': return (
        <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => updateStatus(po.id, 'approved')} data-testid={`approve-po-${po.id}`} title="Approve"><CheckCircle size={14} /></Button>
      );
      case 'approved': return (
        <>
          <Button size="sm" variant="ghost" className="text-violet-400 hover:text-violet-300 h-7 px-2" onClick={() => sendPoWithUploadLink(po.id)} data-testid={`email-po-${po.id}`} title="Email PO with Upload Link"><Mail size={14} /></Button>
          <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => handleMarkPoAsSent(po)} data-testid={`send-po-${po.id}`} title="Mark as Sent"><Send size={14} /></Button>
        </>
      );
      case 'sent': return (
        <>
          <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-7 px-2" onClick={() => updateStatus(po.id, 'completed')} data-testid={`complete-po-${po.id}`} title="Mark as Completed"><Package size={14} /></Button>
          <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => copyUploadLink(po.id)} data-testid={`copy-link-${po.id}`} title="Copy Upload Link"><Link2 size={14} /></Button>
        </>
      );
      default: return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Purchase Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage purchase orders</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="create-po-button">
          <Plus size={16} /> New PO
        </Button>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3" data-testid="po-filter-bar">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by PO # or vendor..."
            className="pl-9 bg-[hsl(var(--surface-input))] border-input text-foreground h-9"
            data-testid="po-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-[hsl(var(--surface-input))] border-input text-foreground h-9" data-testid="po-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {PO_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} className="text-foreground">{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchQuery || statusFilter !== 'all') && (
          <span className="text-xs text-muted-foreground" data-testid="po-result-count">{filteredOrders.length} result{filteredOrders.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">PO #</TableHead>
                <TableHead className="text-muted-foreground">Vendor</TableHead>
                <TableHead className="text-muted-foreground">Amount</TableHead>
                <TableHead className="text-muted-foreground">Date</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8"><Loader2 className="animate-spin inline" size={16} /> Loading...</TableCell></TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{orders.length === 0 ? 'No purchase orders yet.' : 'No orders match your search.'}</TableCell></TableRow>
              ) : filteredOrders.map(po => (
                <TableRow key={po.id} className="border-border hover:bg-muted/50" data-testid={`po-row-${po.id}`}>
                  <TableCell className="text-card-foreground/80 font-mono text-xs">{po.po_number}</TableCell>
                  <TableCell className="text-foreground">{po.vendor_name}</TableCell>
                  <TableCell className="text-foreground font-mono">{formatCurrency(po.amount, po.currency)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(po.created_at)}</TableCell>
                  <TableCell><Badge variant="outline" className={`status-${po.status} text-xs capitalize`}>{po.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => openEditDialog(po)} data-testid={`edit-po-${po.id}`} title="Edit PO"><Pencil size={14} /></Button>
                      {statusActions(po)}
                      <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 h-7 px-2" onClick={() => openPreview(po.id, po.po_number)} data-testid={`preview-po-${po.id}`} title="Preview PO"><Eye size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 h-7 px-2" onClick={() => downloadPdf(po.id, po.po_number)} data-testid={`pdf-po-${po.id}`} title="Download PDF"><FileDown size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400 h-7 px-2" onClick={() => deleteOrder(po.id)} data-testid={`delete-po-${po.id}`} title="Delete"><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="create-po-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground">{editId ? 'Edit Purchase Order' : 'New Purchase Order'}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{editId ? 'Update the purchase order details' : 'Create a purchase order for a vendor'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Vendor Selection */}
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Select Vendor *</Label>
              {vendors.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2" data-testid="no-vendors-msg">
                  No vendors registered. Please add vendors in <a href="/contacts" className="text-blue-500 underline">Clients & Vendors</a> first.
                </div>
              ) : (
                <Select value={form.vendor_id} onValueChange={handleVendorSelect}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="po-vendor-select">
                    <SelectValue placeholder="Choose a registered vendor" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id} className="text-foreground">
                        <span className="font-medium">{v.name}</span>
                        {v.email && <span className="text-muted-foreground ml-2 text-xs">({v.email})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {form.vendor_id && (
              <div className="grid grid-cols-2 gap-4 p-3 border border-border rounded-md bg-muted/30">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Vendor</p>
                  <p className="text-foreground text-sm font-medium mt-0.5">{form.vendor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Email</p>
                  <p className="text-foreground text-sm mt-0.5">{form.vendor_email || '-'}</p>
                </div>
                {form.billing_address && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Address</p>
                    <p className="text-foreground text-sm mt-0.5">{form.billing_address}</p>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="po-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CURRENCY_LIST.map(c => <SelectItem key={c} value={c} className="text-foreground">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="po-description-input" placeholder="PO description" />
              </div>
            </div>
            {/* Line Items with HSN/SAC and GST Tax */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-card-foreground/80 text-sm font-medium">Line Items</Label>
                <Button size="sm" variant="outline" onClick={addItem} className="border-input text-card-foreground/80 h-7 text-xs" data-testid="po-add-item-button"><Plus size={12} className="mr-1" /> Add Item</Button>
              </div>
              {/* Table Header */}
              <div className="grid grid-cols-24 gap-2 px-1">
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">HSN/SAC</span></div>
                <div className="col-span-5"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</span></div>
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Qty</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Rate</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax Type</span></div>
                <div className="col-span-2"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax %</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tax Amt</span></div>
                <div className="col-span-3"><span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</span></div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-24 gap-2 items-center">
                    <div className="col-span-2">
                      <Input value={item.hsn_sac_code} onChange={e => updateItem(idx, 'hsn_sac_code', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="Code" data-testid={`po-item-hsn-${idx}`} />
                    </div>
                    <div className="col-span-5">
                      <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="Item description" data-testid={`po-item-desc-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm text-center" data-testid={`po-item-qty-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm" placeholder="0.00" data-testid={`po-item-price-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <Select value={item.tax_type} onValueChange={v => updateItem(idx, 'tax_type', v)}>
                        <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-xs" data-testid={`po-item-taxtype-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {TAX_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-foreground text-xs">{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.tax_rate} onChange={e => updateItem(idx, 'tax_rate', e.target.value)}
                        className="bg-[hsl(var(--surface-input))] border-input text-foreground h-9 text-sm text-center" placeholder="0"
                        disabled={item.tax_type === 'none'} data-testid={`po-item-tax-${idx}`} />
                    </div>
                    <div className="col-span-3">
                      <div className="h-9 flex items-center text-sm text-muted-foreground font-mono px-2 bg-muted/30 rounded-md border border-transparent">{formatCurrency(item.tax_amount, form.currency)}</div>
                    </div>
                    <div className="col-span-3">
                      <div className="h-9 flex items-center text-sm text-foreground font-mono font-medium px-2 bg-muted/30 rounded-md border border-transparent">{formatCurrency(item.amount, form.currency)}</div>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {form.items.length > 1 && <Button size="sm" variant="ghost" className="text-red-400 h-8 w-8 p-0" onClick={() => removeItem(idx)} title="Remove Item"><Trash2 size={14} /></Button>}
                    </div>
                    {/* CGST/SGST breakdown inline */}
                    {item.tax_type === 'cgst_sgst' && item.tax_rate > 0 && (
                      <div className="col-span-24 -mt-1 mb-1 pl-7">
                        <p className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
                          CGST @ {item.cgst_rate}%: {formatCurrency(item.cgst_amount, form.currency)} &nbsp;&bull;&nbsp; SGST @ {item.sgst_rate}%: {formatCurrency(item.sgst_amount, form.currency)}
                        </p>
                      </div>
                    )}
                    {/* Custom tax name input */}
                    {item.tax_type === 'custom' && (
                      <div className="col-span-24 -mt-1 mb-1 pl-7 flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">Tax name:</span>
                        <input value={item.custom_tax_name || ''} onChange={e => updateItem(idx, 'custom_tax_name', e.target.value)}
                          className="bg-[hsl(var(--surface-input))] border border-input text-foreground rounded px-2 py-0.5 text-xs w-40 focus:border-blue-500 outline-none"
                          placeholder="e.g. VAT, Service Tax, Cess" data-testid={`po-item-customtax-${idx}`} />
                        {item.tax_rate > 0 && <span className="text-[11px] text-muted-foreground font-mono">{item.custom_tax_name || 'Tax'} @ {item.tax_rate}%: {formatCurrency(item.tax_amount, form.currency)}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 pt-3 border-t border-border">
              <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Subtotal: {formatCurrency(form.subtotal, form.currency)}</div>
              {(() => {
                const totalCgst = form.items.reduce((s, i) => s + (i.cgst_amount || 0), 0);
                const totalSgst = form.items.reduce((s, i) => s + (i.sgst_amount || 0), 0);
                const totalIgst = form.items.reduce((s, i) => s + (i.igst_amount || 0), 0);
                const customTaxes = {};
                form.items.filter(i => i.tax_type === 'custom' && i.tax_amount > 0).forEach(i => {
                  const name = i.custom_tax_name || 'Tax';
                  customTaxes[name] = (customTaxes[name] || 0) + i.tax_amount;
                });
                return (
                  <>
                    {totalCgst > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>CGST: {formatCurrency(totalCgst, form.currency)}</div>}
                    {totalSgst > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>SGST: {formatCurrency(totalSgst, form.currency)}</div>}
                    {totalIgst > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>IGST: {formatCurrency(totalIgst, form.currency)}</div>}
                    {Object.entries(customTaxes).map(([name, amt]) => (
                      <div key={name} className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{name}: {formatCurrency(amt, form.currency)}</div>
                    ))}
                    {!totalCgst && !totalSgst && !totalIgst && !Object.keys(customTaxes).length && form.tax_amount > 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Tax: {formatCurrency(form.tax_amount, form.currency)}</div>}
                  </>
                );
              })()}
              <div className="text-lg font-bold text-card-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Total: {formatCurrency(form.amount, form.currency)}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Bank Details</Label>
                <textarea value={form.bank_details} onChange={e => setForm({ ...form, bank_details: e.target.value })}
                  className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="po-bank-details-input" placeholder="Bank name, account number, IFSC..." />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Notes</Label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-[hsl(var(--surface-input))] border border-input text-card-foreground rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  data-testid="po-notes-input" placeholder="Additional notes" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-input text-card-foreground/80">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-po-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editId ? 'Update PO' : 'Create PO')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview */}
      <Dialog open={previewOpen} onOpenChange={(open) => { if (!open && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } setPreviewOpen(open); }}>
        <DialogContent className="max-w-4xl bg-card border-border h-[90vh] flex flex-col" data-testid="preview-dialog-po">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-card-foreground flex items-center gap-2"><Eye size={18} /> PO Preview — {previewNumber}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Preview the purchase order before downloading</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 rounded-md overflow-hidden border border-border bg-white">
            {previewUrl && <iframe src={previewUrl + "#toolbar=1&navpanes=0"} className="w-full h-full" title="PDF Preview" style={{border:"none"}} />}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-input text-card-foreground/80">Close</Button>
            <Button onClick={() => { if (previewUrl) { const a = document.createElement('a'); a.href = previewUrl; a.download = `${previewNumber}.pdf`; a.click(); } }} className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><FileDown size={14} /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Mark as Sent Dialog */}
      <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="confirm-send-po-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground flex items-center gap-2"><Send size={18} className="text-blue-400" /> Mark PO as Sent</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Would you also like to email this PO to <span className="text-foreground font-medium">{confirmSendPo?.vendor_email || 'the vendor'}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setConfirmSendOpen(false); setConfirmSendPo(null); }} className="border-input text-card-foreground/80" data-testid="confirm-po-send-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => confirmMarkPoAsSent(false)} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid="confirm-po-send-only">
              <Send size={14} className="mr-2" /> Mark as Sent Only
            </Button>
            <Button onClick={() => confirmMarkPoAsSent(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="confirm-po-send-and-email">
              <Mail size={14} className="mr-2" /> Mark as Sent & Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}